import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/internal/operators/take';
import { UtilsService } from '../../../../shared/services/utils.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { NewsItemClipResult, NewsItemResponse, NewsItemReviewStatus, RecordingResponse, RecordingService } from '../../../../shared/services/recording.service';

const ZONE = 'Europe/Skopje';
// "▶ end" plays the last seconds of an item and a little of what follows, then stops.
const END_PREVIEW_BEFORE = 10;
const END_PREVIEW_AFTER = 4;
const POLL_MS = 15_000;

// Editor's check of one recorded edition: play each AI-found item's start and end, mark it
// right / boundary off / merged / not an item, count missing items, mark the edition reviewed.
@Component({
	selector: 'app-recording-review',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule, MatCardModule, MatCheckboxModule, MatDividerModule, MatIconModule, MatButtonModule, MatTooltipModule, MatProgressSpinnerModule, TranslateModule],
	templateUrl: './recording-review.component.html',
	styleUrl: './recording-review.component.scss',
})
export class RecordingReviewComponent implements OnInit, OnDestroy {
	@ViewChild('player') player?: ElementRef<HTMLVideoElement>;

	readonly verdicts: NewsItemReviewStatus[] = ['OK', 'BOUNDARY', 'MERGED', 'EXTRA'];

	recording: RecordingResponse | null = null;
	items: NewsItemResponse[] = [];
	loading = false;
	saving = false;
	retranscribing = false;
	activeItemId: string | null = null;
	// "Текст" button toggles this inline per item - read-only, no dialog needed.
	expandedTextItemId: string | null = null;

	// Phase 6: "Креирај клипови". Selection defaults to every item that doesn't have a clip yet and
	// isn't EXTRA (see load()). Checked items lock (no checkbox, just "✓ Клип креиран") once a clip
	// exists - never unchecked by the user, only by the server's response.
	selectedItemIds = new Set<string>();
	creatingClips = false;
	// One-time feedback (author match, etc.) from the last create call, keyed by item id - not
	// persisted, just what the editor needs to see right after clicking "Креирај клипови".
	clipResultByItemId: Record<string, NewsItemClipResult> = {};
	// "Преглед" toggle for the small clip summary (id, title, medium) - same pattern as expandedTextItemId.
	expandedClipItemId: string | null = null;

	// Ѓ1 - boundary edit. A plain text mm:ss (or hh:mm:ss) pair, not a form group - only one
	// item's editor is open at a time, same pattern as expandedTextItemId/expandedClipItemId.
	editingBoundariesItemId: string | null = null;
	editStartText = '';
	editEndText = '';
	boundariesError: string | null = null;
	savingBoundaries = false;

	// Е1 - "Раздели". One row per new part (the FIRST part is the item itself and is never in
	// this list) - each row is a split point (mm:ss, where the next part starts) plus that part's
	// title. Up to 4 extra parts (5 total), enforced server-side too.
	readonly maxSplitPoints = 4;
	splittingItemId: string | null = null;
	splitPointTexts: string[] = [];
	splitTitleTexts: string[] = [];
	splitError: string | null = null;
	splitting = false;

	private id = '';
	private stopAt: number | null = null;
	private pollTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private route: ActivatedRoute,
		private recordingService: RecordingService,
		private utils: UtilsService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private dialog: MatDialog
	) {}

	ngOnInit(): void {
		this.id = this.route.snapshot.paramMap.get('id') || '';
		this.load();
	}

	ngOnDestroy(): void {
		this.clearPoll();
	}

	load() {
		this.clearPoll();
		this.loading = true;
		this.recordingService
			.getRecording(this.id)
			.pipe(take(1))
			.subscribe({
				next: recording => {
					this.recording = recording;
					this.recordingService
						.getItems(this.id)
						.pipe(take(1))
						.subscribe({
							next: items => {
								this.items = items;
								// Repeat airings ("🔁") start unchecked by default - see RECORDING_REVIEW docs.
								this.selectedItemIds = new Set(this.eligibleItems().filter(i => !i.duplicateOfNewsItemId).map(i => i.id));
								this.loading = false;
								this.pollWhileBusy();
							},
							error: () => (this.loading = false),
						});
				},
				error: () => (this.loading = false),
			});
	}

	get busy(): boolean {
		const r = this.recording;
		return !!r && (r.status === 'RECORDING' || r.status === 'SEGMENTING' || r.status === 'UPLOADING' || r.segmentationStatus === 'RUNNING');
	}

	// Transcription runs in the background after the edition is already DONE/SHORT, so it is
	// deliberately not part of `busy` (which gates the player/review controls) - only the polling
	// needs to know about it, to pick up the finished text without a manual refresh.
	get transcribing(): boolean {
		return this.recording?.transcriptionStatus === 'RUNNING';
	}

	toggleText(item: NewsItemResponse) {
		this.expandedTextItemId = this.expandedTextItemId === item.id ? null : item.id;
	}

	toggleClipPreview(item: NewsItemResponse) {
		this.expandedClipItemId = this.expandedClipItemId === item.id ? null : item.id;
	}

	// Items a clip can still be created for - no clip yet, and not EXTRA (never a real news item).
	eligibleItems(): NewsItemResponse[] {
		return this.items.filter(i => !i.clipId && i.reviewStatus !== 'EXTRA');
	}

	allEligibleSelected(): boolean {
		const eligible = this.eligibleItems();
		return eligible.length > 0 && eligible.every(i => this.selectedItemIds.has(i.id));
	}

	toggleSelectAll() {
		const eligible = this.eligibleItems();
		if (this.allEligibleSelected()) {
			eligible.forEach(i => this.selectedItemIds.delete(i.id));
		} else {
			eligible.forEach(i => this.selectedItemIds.add(i.id));
		}
	}

	toggleItemSelection(item: NewsItemResponse) {
		if (this.selectedItemIds.has(item.id)) {
			this.selectedItemIds.delete(item.id);
		} else {
			this.selectedItemIds.add(item.id);
		}
	}

	authorFeedback(item: NewsItemResponse): string | null {
		const result = this.clipResultByItemId[item.id];
		if (!result || !result.aiAuthorName) {
			return null;
		}
		if (result.matchedAuthorName) {
			const base = this.translate.instant('RECORDING_REVIEW.AUTHOR_MATCHED', { aiName: result.aiAuthorName, author: result.matchedAuthorName });
			return result.authorAmbiguous ? `${base} — ${this.translate.instant('RECORDING_REVIEW.AUTHOR_AMBIGUOUS')}` : base;
		}
		return this.translate.instant('RECORDING_REVIEW.AUTHOR_NOT_FOUND', { aiName: result.aiAuthorName });
	}

	// "Креирај клипови (N)" - one confirm dialog, worded differently when some of the selected
	// items have no text yet (the only case where the server is allowed to create a clip without
	// one, and only because the editor confirmed it here first).
	createClips() {
		const ids = Array.from(this.selectedItemIds);
		if (ids.length === 0) {
			return;
		}
		const withoutText = this.items.some(i => ids.includes(i.id) && (!i.text || !i.text.trim()));
		const message = this.translate.instant(withoutText ? 'RECORDING_REVIEW.CREATE_CLIPS_WITHOUT_TEXT_CONFIRM' : 'RECORDING_REVIEW.CREATE_CLIPS_CONFIRM', { count: ids.length });

		this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: { mode: 'USER_ACTION', type: 'custom-fields', message, fields: [] },
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe(confirmed => {
				if (!confirmed) {
					return;
				}
				this.creatingClips = true;
				this.recordingService
					.createClipsFromItems(this.id, ids, withoutText)
					.pipe(take(1))
					.subscribe({
						next: results => this.applyClipResults(results),
						error: (err: HttpErrorResponse) => {
							this.creatingClips = false;
							this.sharedService.showNotification.next({
								message: err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
								type: 'ERROR',
							});
						},
					});
			});
	}

	playStart(item: NewsItemResponse) {
		this.activeItemId = item.id;
		this.stopAt = null;
		this.seek(item.startSeconds);
	}

	playEnd(item: NewsItemResponse) {
		this.activeItemId = item.id;
		this.stopAt = item.endSeconds + END_PREVIEW_AFTER;
		this.seek(Math.max(item.startSeconds, item.endSeconds - END_PREVIEW_BEFORE));
	}

	onTimeUpdate() {
		const video = this.player?.nativeElement;
		if (video && this.stopAt !== null && video.currentTime >= this.stopAt) {
			video.pause();
			this.stopAt = null;
		}
	}

	setVerdict(item: NewsItemResponse, verdict: NewsItemReviewStatus) {
		const next = item.reviewStatus === verdict ? null : verdict;
		const previous = item.reviewStatus;
		item.reviewStatus = next;
		this.recordingService
			.reviewItem(this.id, item.id, next)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					item.reviewStatus = updated.reviewStatus;
					this.refreshCounts();
				},
				error: () => (item.reviewStatus = previous),
			});
	}

	changeMissing(delta: number) {
		if (!this.recording) {
			return;
		}
		const value = Math.max(0, (this.recording.missingItems || 0) + delta);
		this.saveReview(value, !!this.recording.reviewedAt);
	}

	toggleDone() {
		if (!this.recording) {
			return;
		}
		this.saveReview(this.recording.missingItems || 0, !this.recording.reviewedAt);
	}

	resegment() {
		this.saving = true;
		// Shown immediately on click, not after the response - the button's own spinner plus the
		// RUNNING banner below take over from there, but the click itself needs instant feedback.
		this.sharedService.showNotification.next({ message: this.translate.instant('RECORDING_REVIEW.RESEGMENT_STARTED'), type: 'SUCCESS' });
		this.recordingService
			.resegment(this.id)
			.pipe(take(1))
			.subscribe({
				next: recording => {
					this.recording = recording;
					this.saving = false;
					this.pollWhileBusy();
				},
				error: (err: HttpErrorResponse) => {
					this.saving = false;
					this.sharedService.showNotification.next({
						message: err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	// "Повтори транскрипција" - re-runs just the transcription step (never touches item boundaries
	// from segmentation). Same immediate-feedback pattern as resegment().
	retranscribe() {
		this.retranscribing = true;
		this.sharedService.showNotification.next({ message: this.translate.instant('RECORDING_REVIEW.RETRANSCRIBE_STARTED'), type: 'SUCCESS' });
		this.recordingService
			.retranscribe(this.id)
			.pipe(take(1))
			.subscribe({
				next: recording => {
					this.recording = recording;
					this.retranscribing = false;
					this.pollWhileBusy();
				},
				error: () => (this.retranscribing = false),
			});
	}

	mediumName(): string {
		return this.recording ? this.utils.getParameterByLanguageScript(this.recording, 'mediumName') || this.recording.mediumNameLatin || '' : '';
	}

	formatDateTime(iso: string | null | undefined): string {
		if (!iso) {
			return '';
		}
		return new Intl.DateTimeFormat('mk-MK', { timeZone: ZONE, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
	}

	// Just "ЧЧ:мм", for the "🔁 Повторено/Веќе постои клип" badge.
	hhmm(iso: string | null | undefined): string {
		if (!iso) {
			return '';
		}
		return new Intl.DateTimeFormat('mk-MK', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
	}

	// "Не е повторување" - undoes a repeat-airing match; the item goes back to behaving like any
	// other prilog (including re-joining the default-checked selection, if it is still eligible).
	markNotDuplicate(item: NewsItemResponse) {
		this.recordingService
			.markNotDuplicate(this.id, item.id)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					item.duplicateOfNewsItemId = updated.duplicateOfNewsItemId;
					item.matchPercentage = updated.matchPercentage;
					item.notDuplicate = updated.notDuplicate;
					item.anchorRecordingId = updated.anchorRecordingId;
					item.anchorClipId = updated.anchorClipId;
					item.anchorAiredAt = updated.anchorAiredAt;
					if (!item.clipId && item.reviewStatus !== 'EXTRA') {
						this.selectedItemIds.add(item.id);
					}
				},
				error: (err: HttpErrorResponse) => {
					this.sharedService.showNotification.next({
						message: err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	// Ѓ1 - boundary edit from "Провери".
	toggleBoundaryEdit(item: NewsItemResponse) {
		if (this.editingBoundariesItemId === item.id) {
			this.editingBoundariesItemId = null;
			return;
		}
		this.editingBoundariesItemId = item.id;
		this.editStartText = this.mmss(item.startSeconds);
		this.editEndText = this.mmss(item.endSeconds);
		this.boundariesError = null;
	}

	setStartFromPlayer() {
		const video = this.player?.nativeElement;
		if (video) {
			this.editStartText = this.mmss(video.currentTime);
		}
	}

	setEndFromPlayer() {
		const video = this.player?.nativeElement;
		if (video) {
			this.editEndText = this.mmss(video.currentTime);
		}
	}

	saveBoundaries(item: NewsItemResponse) {
		const startSeconds = this.parseTime(this.editStartText);
		const endSeconds = this.parseTime(this.editEndText);
		if (startSeconds === null || endSeconds === null) {
			this.boundariesError = this.translate.instant('RECORDING_REVIEW.BOUNDARIES_INVALID_FORMAT');
			return;
		}
		if (startSeconds >= endSeconds) {
			this.boundariesError = this.translate.instant('RECORDING_REVIEW.BOUNDARIES_START_BEFORE_END');
			return;
		}
		this.savingBoundaries = true;
		this.boundariesError = null;
		this.recordingService
			.updateItemBoundaries(this.id, item.id, startSeconds, endSeconds)
			.pipe(take(1))
			.subscribe({
				next: updated => {
					item.startSeconds = updated.startSeconds;
					item.endSeconds = updated.endSeconds;
					item.originalStartSeconds = updated.originalStartSeconds;
					item.originalEndSeconds = updated.originalEndSeconds;
					this.savingBoundaries = false;
					this.editingBoundariesItemId = null;
				},
				error: (err: HttpErrorResponse) => {
					this.savingBoundaries = false;
					this.boundariesError = err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR');
				},
			});
	}

	// Е1 - "Раздели".
	toggleSplit(item: NewsItemResponse) {
		if (this.splittingItemId === item.id) {
			this.splittingItemId = null;
			return;
		}
		this.splittingItemId = item.id;
		this.splitPointTexts = [''];
		this.splitTitleTexts = [`${item.title || ''} (2)`];
		this.splitError = null;
	}

	addSplitPoint(item: NewsItemResponse) {
		if (this.splitPointTexts.length >= this.maxSplitPoints) {
			return;
		}
		this.splitPointTexts.push('');
		this.splitTitleTexts.push(`${item.title || ''} (${this.splitPointTexts.length + 1})`);
	}

	removeSplitPoint(index: number) {
		this.splitPointTexts.splice(index, 1);
		this.splitTitleTexts.splice(index, 1);
	}

	submitSplit(item: NewsItemResponse) {
		const points: number[] = [];
		for (const text of this.splitPointTexts) {
			const seconds = this.parseTime(text);
			if (seconds === null) {
				this.splitError = this.translate.instant('RECORDING_REVIEW.BOUNDARIES_INVALID_FORMAT');
				return;
			}
			points.push(seconds);
		}
		for (let i = 0; i < points.length; i++) {
			if (points[i] <= item.startSeconds || points[i] >= item.endSeconds) {
				this.splitError = this.translate.instant('RECORDING_REVIEW.SPLIT_POINT_OUTSIDE_ITEM');
				return;
			}
			if (i > 0 && points[i] <= points[i - 1]) {
				this.splitError = this.translate.instant('RECORDING_REVIEW.SPLIT_POINTS_NOT_INCREASING');
				return;
			}
		}
		const titles = this.splitTitleTexts.map(t => t.trim());
		if (titles.some(t => !t)) {
			this.splitError = this.translate.instant('RECORDING_REVIEW.SPLIT_TITLE_REQUIRED');
			return;
		}

		this.splitting = true;
		this.splitError = null;
		this.recordingService
			.splitItem(this.id, item.id, points, titles)
			.pipe(take(1))
			.subscribe({
				next: items => {
					this.items = items;
					this.selectedItemIds = new Set(this.eligibleItems().filter(i => !i.duplicateOfNewsItemId).map(i => i.id));
					this.splitting = false;
					this.splittingItemId = null;
				},
				error: (err: HttpErrorResponse) => {
					this.splitting = false;
					this.splitError = err.error?.message || this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR');
				},
			});
	}

	// Accepts "mm:ss" or "hh:mm:ss" - minutes/hours have no upper digit limit (an edition can run
	// well past 59 minutes), seconds (and minutes in the hh:mm:ss form) must be 00-59.
	private parseTime(text: string): number | null {
		const parts = text.trim().split(':');
		if (parts.length === 2) {
			const [m, s] = parts;
			if (!/^\d+$/.test(m) || !/^\d{1,2}$/.test(s) || Number(s) > 59) {
				return null;
			}
			return Number(m) * 60 + Number(s);
		}
		if (parts.length === 3) {
			const [h, m, s] = parts;
			if (!/^\d+$/.test(h) || !/^\d{1,2}$/.test(m) || Number(m) > 59 || !/^\d{1,2}$/.test(s) || Number(s) > 59) {
				return null;
			}
			return Number(h) * 3600 + Number(m) * 60 + Number(s);
		}
		return null;
	}

	mmss(seconds: number | null | undefined): string {
		if (seconds === null || seconds === undefined) {
			return '–';
		}
		const total = Math.round(seconds);
		return `${Math.floor(total / 60).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
	}

	reviewedCount(): number {
		return this.items.filter(i => !!i.reviewStatus).length;
	}

	private saveReview(missing: number, done: boolean) {
		this.saving = true;
		this.recordingService
			.reviewRecording(this.id, missing, done)
			.pipe(take(1))
			.subscribe({
				next: recording => {
					this.recording = recording;
					this.saving = false;
				},
				error: () => (this.saving = false),
			});
	}

	private applyClipResults(results: NewsItemClipResult[]) {
		this.creatingClips = false;
		let created = 0;
		const failures: string[] = [];
		for (const result of results) {
			const item = this.items.find(i => i.id === result.itemId);
			if (!item) {
				continue;
			}
			this.selectedItemIds.delete(item.id);
			if (result.outcome === 'CREATED' || result.outcome === 'ALREADY_EXISTS') {
				item.clipId = result.clipId;
				this.clipResultByItemId[item.id] = result;
				if (result.outcome === 'CREATED') {
					created++;
				}
			} else if (result.outcome === 'ERROR') {
				failures.push(item.title || item.id);
			}
		}

		const message =
			failures.length === 0
				? this.translate.instant('RECORDING_REVIEW.CREATE_CLIPS_RESULT', { created, total: results.length })
				: `${this.translate.instant('RECORDING_REVIEW.CREATE_CLIPS_RESULT', { created, total: results.length })} ${this.translate.instant('RECORDING_REVIEW.CREATE_CLIPS_FAILURES', { titles: failures.join(', ') })}`;
		this.sharedService.showNotification.next({ message, type: failures.length === 0 ? 'SUCCESS' : 'ERROR' });
	}

	private refreshCounts() {
		if (this.recording) {
			this.recording.reviewedItemCount = this.reviewedCount();
		}
	}

	private seek(seconds: number) {
		const video = this.player?.nativeElement;
		if (!video) {
			return;
		}
		video.currentTime = seconds;
		video.play().catch(() => undefined);
	}

	private pollWhileBusy() {
		this.clearPoll();
		if (this.busy || this.transcribing) {
			this.pollTimer = setTimeout(() => this.load(), POLL_MS);
		}
	}

	private clearPoll() {
		if (this.pollTimer) {
			clearTimeout(this.pollTimer);
			this.pollTimer = null;
		}
	}
}
